import * as React from "react";
import { GetSnapshotEventGroupedResponseItem } from "@/modules/snapshots/businessReports.types";
import { ChartGridLines, StackedStatusBar } from "@/modules/snapshots/snapshotEvents/components/stackedStatusBar";
import { useBarTooltip } from "@/modules/snapshots/snapshotEvents/components/useBarTooltip";

interface EventItem {
  total: number;
  completed: number;
  inProgress: number;
  overdue: number;
}

interface Props {
  groupedEvents: Record<string, Array<GetSnapshotEventGroupedResponseItem>>;
  getStatusColor: (status: string) => string;
}

export const ActionTakerWorkloadChart: React.FC<Props> = ({ groupedEvents, getStatusColor }) => {
  const { hoveredIndex: hoveredBar, tooltipPosition, handleMouseEnter, handleMouseLeave } = useBarTooltip();

  const recipients = Object.values(groupedEvents)
    .flat()
    .map(e => ({ ...e.recipients, status: e.status === "overdue" ? "overdue" : e.status }))
    .flat();

  if (recipients.length === 0) {
    return (
      <div className="rounded-lg border border-[#e9e0d5] bg-white p-5 text-center text-muted-foreground">
        No data available
      </div>
    );
  }

  // Calculate workload by action taker
  const workloadData = recipients.reduce<Record<string, EventItem>>((acc, event) => {
    const actionTaker = event.email;
    if (!acc[actionTaker]) {
      acc[actionTaker] = { total: 0, completed: 0, inProgress: 0, overdue: 0 };
    }
    acc[actionTaker].total++;
    switch (event.status) {
      case "done":
        acc[actionTaker].completed++;
        break;
      case "in_progress":
        acc[actionTaker].inProgress++;
        break;
      case "overdue":
        acc[actionTaker].overdue++;
        break;
    }
    return acc;
  }, {});

  const sortedWorkload = Object.entries(workloadData)
    .map(([actionTaker, data]) => ({ actionTaker, ...data }))
    .sort((a, b) => b.total - a.total)
    .slice(0, 12);

  // Chart dimensions
  const chartWidth = 400;
  const chartHeight = 240;
  const margin = { top: 20, right: 20, bottom: 20, left: 40 };
  const innerWidth = chartWidth - margin.left - margin.right;
  const innerHeight = chartHeight - margin.top - margin.bottom;

  const maxValue = Math.max(...sortedWorkload.map(d => d.total), 1);
  const barWidth = (innerWidth / sortedWorkload.length) * 0.5;
  const barSpacing = innerWidth / sortedWorkload.length;
  const xScale = (index: number) => margin.left + index * barSpacing + (barSpacing - barWidth) / 2;

  return (
    <div className="relative flex min-h-[280px] w-full flex-col rounded-lg border border-[#e9e0d5] bg-white p-4 shadow-sm">
      {/* Header */}
      <div className="mb-4 text-center">
        <h3 className="m-0 text-base font-semibold text-[#0a213e]">Action Taker Workload</h3>
      </div>

      {/* Chart Container */}
      <div className="relative flex justify-center">
        <svg width={chartWidth} height={chartHeight} style={{ overflow: "visible" }}>
          <ChartGridLines margin={margin} innerWidth={innerWidth} innerHeight={innerHeight} />

          {/* Stacked Bars */}
          {sortedWorkload.map((data, index) => (
            <StackedStatusBar
              key={`bar-${index}`}
              x={xScale(index)}
              baseY={margin.top + innerHeight}
              barWidth={barWidth}
              totalBarHeight={(data.total / maxValue) * innerHeight}
              total={data.total}
              done={data.completed}
              inProgress={data.inProgress}
              overdue={data.overdue}
              isHovered={hoveredBar === index}
              getStatusColor={getStatusColor}
              onMouseEnter={e => handleMouseEnter(e, index)}
              onMouseLeave={handleMouseLeave}
              renderLabel={topY => (
                <text
                  x={xScale(index) + barWidth / 2}
                  y={topY - 5}
                  textAnchor="middle"
                  style={{
                    fontSize: "10px",
                    fill: "#0a213e",
                    fontWeight: 600,
                    fontFamily: "Inter, sans-serif",
                  }}
                >
                  {data.total}
                </text>
              )}
            />
          ))}
        </svg>

        {/* Tooltip */}
        {hoveredBar !== null && (
          <div
            className="pointer-events-none absolute z-[1000] min-w-[200px] rounded-lg border border-[#e9e0d5] bg-white p-4 text-sm shadow-lg"
            style={{
              left: tooltipPosition.x,
              top: tooltipPosition.y,
              fontSize: "14px",
              whiteSpace: "nowrap",
            }}
          >
            <div className="mb-3 text-base font-semibold text-[#0a213e]">{sortedWorkload[hoveredBar].actionTaker}</div>
            <div className="mb-2 text-[15px] text-[#3c3c41]">Total: {sortedWorkload[hoveredBar].total} events</div>
            <div className="text-[14px] text-[#8b92af]">
              <div className="mb-1 flex items-center gap-2">
                <div className="h-3 w-3 rounded-full" style={{ backgroundColor: getStatusColor("completed") }} />
                Completed: {sortedWorkload[hoveredBar].completed}
              </div>
              <div className="mb-1 flex items-center gap-2">
                <div className="h-3 w-3 rounded-full" style={{ backgroundColor: getStatusColor("in-progress") }} />
                In-Progress: {sortedWorkload[hoveredBar].inProgress}
              </div>
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full" style={{ backgroundColor: getStatusColor("overdue") }} />
                Overdue: {sortedWorkload[hoveredBar].overdue}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
